import { IRailingConfig, IStepGeometry, IStringerType } from '@stair-craft/common/util';

import { RESIDENTIAL_NORMS } from '../../norms';
import { IPlanPoint } from '../shared-plan-geometry/plan-geometry';
import { treadFootprint } from '../shared-tread-footprint/tread-footprint';

/** The widest clear gap allowed between uprights: the set spacing, never above the norm. */
export function clearBalusterSpacing(balusterSpacing: number): number {
    return Math.min(balusterSpacing, RESIDENTIAL_NORMS.balusterSpacingMax);
}

/**
 * How many equal slots a span is cut into, so that one baluster per slot leaves at most the
 * allowed clear gap between neighbours: `ceil(span / (spacing + width))`, never below `min`.
 */
export function balusterCount(span: number, railing: IRailingConfig, min: number = 1): number {
    const pitch: number = clearBalusterSpacing(railing.balusterSpacing) + railing.balusterWidth;
    return Math.max(min, Math.ceil(span / pitch - 1e-6));
}

/**
 * The railing with one baluster pitch for the flights and the level runs alike: the tread going
 * cut into as many equal parts as keep the clear gap within the set spacing and the norm. Its
 * spacing is the clear gap of that pitch, so a level run counted by `balusterCount` gets the
 * flight's pitch too.
 */
export function evenPitchRailing(railing: IRailingConfig, going: number): IRailingConfig {
    if (going <= 0) {
        return railing;
    }
    const perTread: number = balusterCount(going, railing);
    return { ...railing, balusterSpacing: going / perTread - railing.balusterWidth };
}

/**
 * Where the balusters of a flight stand along its run between two post faces, `from` (the flight
 * bottom) to `to`, either way: every tread's open going, between its nose and the next one, is cut
 * into as many equal parts as keep the gap within the spacing, a baluster in the middle of each.
 * A baluster that does not fit between the post faces is left out.
 */
export function treadBalusterRuns(
    from: number,
    to: number,
    treads: readonly IStepGeometry[],
    flight: { readonly stringerType: IStringerType; readonly axis: 'x' | 'z' },
    railing: IRailingConfig
): number[] {
    const span: number = Math.abs(to - from);
    const half: number = railing.balusterWidth / 2;
    const toward: number = Math.sign(to - from) || 1;
    let room: number = Infinity;
    const runs: number[] = treads
        .flatMap((step: IStepGeometry): number[] => {
            const along: number[] = treadFootprint(step, flight.stringerType).map(
                (point: IPlanPoint) => toward * (point[flight.axis] - from)
            );
            const low: number = Math.min(...along);
            // The footprint runs from the tread's own nose to its back, under the next tread's nose.
            const open: number = Math.max(...along) - low - step.noseLength;
            const perTread: number = balusterCount(open, railing);
            room = Math.min(room, open / perTread / 2 - half);
            return Array.from({ length: perTread }, (_: unknown, k: number): number => low + (open * (k + 0.5)) / perTread);
        })
        .filter((run: number) => run - half >= 0 && run + half <= span);
    if (runs.length === 0) {
        return [];
    }
    // The row slides within its parts so the gaps at the two post faces are equal.
    const ends: number = span - runs[runs.length - 1] - half - (runs[0] - half);
    const shift: number = Math.max(-room, Math.min(room, ends / 2));
    return runs.map((run: number) => from + toward * (run + shift));
}
